/**
 * Token Provider for FHIR Client Integration
 *
 * This module provides token management with:
 * - Proactive token refresh before expiration
 * - Type-safe token handling
 *
 * Note: Tokens are stored in-memory only (not in sessionStorage/localStorage).
 * On page refresh, tokens are restored via the HTTP-only refresh cookie.
 * This provides better security as tokens are not visible in DevTools Storage.
 */

/**
 * Refresh token 5 minutes before expiry to prevent
 * requests failing due to token expiration
 */
const TOKEN_REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * MFA Challenge returned when additional authentication is required
 * @deprecated Use AuthChallenge instead - this interface doesn't match backend response
 */
export interface MfaChallenge {
  challengeName: string;
  session: string;
  challengeParameters?: Record<string, string>;
}

/**
 * Authentication challenge returned when additional action is required
 * Matches Cognito challenge response structure from backend
 */
export interface AuthChallenge {
  name: string;
  parameters?: Record<string, string>;
}

/**
 * MFA challenge type names returned by the backend
 */
export const MFA_CHALLENGE_TYPES = {
  /** User needs to set up MFA (first-time setup, tokens provided) */
  MFA_SETUP: "MFA_SETUP",
  /** User needs to choose MFA method during setup */
  SELECT_MFA_TYPE: "SELECT_MFA_TYPE",
  /** User needs to enter SMS verification code */
  SMS_MFA: "SMS_MFA",
  /** User needs to enter TOTP code from authenticator app */
  SOFTWARE_TOKEN_MFA: "SOFTWARE_TOKEN_MFA",
  /** User needs to enter email OTP code */
  EMAIL_OTP: "EMAIL_OTP",
  /** User needs to set a new password (already implemented) */
  NEW_PASSWORD_REQUIRED: "NEW_PASSWORD_REQUIRED",
} as const;

export type MfaChallengeType = (typeof MFA_CHALLENGE_TYPES)[keyof typeof MFA_CHALLENGE_TYPES];

/**
 * Authentication tokens stored after login
 *
 * Cognito Login Response Structure:
 * ```json
 * {
 *   "access": "<JWT access token>",
 *   "id": "<JWT id token>",
 *   "valid": true,
 *   "challenge": null | { challengeName, session, ... }
 * }
 * ```
 *
 * Access Token Claims: sub, username, cognito:groups, token_use, scope, exp, iat
 * ID Token Claims: sub, cognito:username, cognito:groups, email, email_verified, exp, iat
 */
export interface AuthTokens {
  /** JWT access token for API authorization */
  accessToken: string;
  /** JWT ID token containing user claims (email, groups, etc.) */
  idToken?: string;
  /** Refresh token for obtaining new access tokens */
  refreshToken?: string;
  /** Token expiration timestamp (ms since epoch) */
  expiresAt?: number;
  /** Token type (typically "Bearer") */
  tokenType?: string;
  /** Whether the authentication was successful */
  valid?: boolean;
  /** MFA challenge if additional authentication is required */
  challenge?: MfaChallenge | null;
}

// In-memory token storage - cleared on page refresh, restored via /refresh endpoint
let cachedTokens: AuthTokens | null = null;

/**
 * Event types for token changes
 */
export type TokenChangeEvent = {
  type: "updated" | "cleared" | "expired";
  tokens: AuthTokens | null;
  source: "local" | "external"; // local = this tab, external = another tab
};

/**
 * Token change listener type
 */
export type TokenChangeListener = (event: TokenChangeEvent) => void;

// Listeners for token changes
const tokenChangeListeners: Set<TokenChangeListener> = new Set();

/**
 * Subscribe to token changes (including from other tabs)
 *
 * @param listener - Callback function for token changes
 * @returns Unsubscribe function
 */
export const subscribeToTokenChanges = (listener: TokenChangeListener): (() => void) => {
  tokenChangeListeners.add(listener);
  return () => {
    tokenChangeListeners.delete(listener);
  };
};

/**
 * Notify all listeners of a token change
 */
const notifyTokenChange = (event: TokenChangeEvent): void => {
  tokenChangeListeners.forEach(listener => {
    try {
      listener(event);
    } catch (error) {
      console.error("Error in token change listener:", error);
    }
  });
};

/**
 * Store authentication tokens in memory
 */
export const setStoredTokens = (tokens: AuthTokens | null): void => {
  cachedTokens = tokens;
  notifyTokenChange({
    type: tokens ? "updated" : "cleared",
    tokens,
    source: "local",
  });
};

/**
 * Retrieve authentication tokens from memory
 */
export const getStoredTokens = (): AuthTokens | null => {
  return cachedTokens;
};

/**
 * Check if the stored token is expired
 */
export const isTokenExpired = (tokens: AuthTokens | null): boolean => {
  if (!tokens || !tokens.expiresAt) {
    return true;
  }
  return Date.now() > tokens.expiresAt;
};

/**
 * Check if the token is expiring soon (within threshold)
 * Used for proactive token refresh
 */
export const isTokenExpiringSoon = (tokens: AuthTokens | null): boolean => {
  if (!tokens?.expiresAt) {
    return false; // Can't determine if no expiry set
  }
  return Date.now() >= tokens.expiresAt - TOKEN_REFRESH_THRESHOLD_MS;
};

/**
 * Get time until token expiration in milliseconds
 * Returns 0 if already expired, null if no expiration set
 */
export const getTimeUntilExpiry = (tokens: AuthTokens | null): number | null => {
  if (!tokens?.expiresAt) {
    return null;
  }
  const remaining = tokens.expiresAt - Date.now();
  return Math.max(0, remaining);
};

/**
 * Clear authentication tokens from memory
 */
export const clearTokens = (): void => {
  setStoredTokens(null);
};

/**
 * Get the current access token synchronously
 * Used for auth-related operations (e.g., logout)
 */
export const getAccessToken = (): string | null => {
  const tokens = getStoredTokens();
  return tokens?.accessToken ?? null;
};

/**
 * Get the current id token synchronously
 * Used for FHIR requests
 */
export const getIdToken = (): string | null => {
  const tokens = getStoredTokens();
  return tokens?.idToken ?? null;
};
